import type { CalendarFeed, CalendarItem, HolidayItem } from "@ronin/contracts";
import { addDays, type Span, timedSegments, zonedParts } from "@ronin/core";

/** A non-holiday item as a span of local dates (month view, all-day section). */
export interface ItemSpan extends Span {
  item: Exclude<CalendarItem, HolidayItem>;
  /** Local start time ("18:00") for timed items, null for all-day ones. */
  time: string | null;
}

/** A timed item's part on one local day (minutes since midnight). */
export interface ItemBlock {
  id: string;
  item: Exclude<CalendarItem, HolidayItem>;
  start: number;
  end: number;
  /** The event goes on before / after this day. */
  continuesBefore: boolean;
  continuesAfter: boolean;
}

export interface DayHolidays {
  public: HolidayItem[];
  school: HolidayItem[];
}

/** Feed items of hidden or removed feeds disappear at once, before the server is asked again. */
export function visibleItems(items: readonly CalendarItem[], feeds: readonly CalendarFeed[]) {
  const visible = new Set(feeds.filter((f) => f.visible).map((f) => f.id));
  return items.filter((item) => item.source !== "feed" || visible.has(item.feedId));
}

const isHoliday = (item: CalendarItem): item is HolidayItem => item.source === "holiday";

/**
 * Month view: every non-holiday item as a span of local dates. All-day items come first,
 * then timed items by start. A timed item ending exactly at midnight does not touch the
 * next day.
 */
export function monthSpans(items: readonly CalendarItem[], timeZone: string): ItemSpan[] {
  const allDay: ItemSpan[] = [];
  const timed: Array<ItemSpan & { at: number }> = [];
  for (const item of items) {
    if (isHoliday(item)) continue;
    if (item.allDay && item.startDate && item.endDate) {
      allDay.push({
        id: item.id,
        item,
        startDate: item.startDate,
        endDate: item.endDate,
        time: null,
      });
    } else if (item.startsAt && item.endsAt) {
      const start = zonedParts(new Date(item.startsAt), timeZone);
      const end = zonedParts(new Date(item.endsAt), timeZone);
      const endDate = end.minutes === 0 && end.date > start.date ? addDays(end.date, -1) : end.date;
      timed.push({
        id: item.id,
        item,
        startDate: start.date,
        endDate,
        time: start.time,
        at: Date.parse(item.startsAt),
      });
    }
  }
  allDay.sort((a, b) => (a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0));
  timed.sort((a, b) => a.at - b.at);
  return [...allDay, ...timed.map(({ at: _at, ...span }) => span)];
}

/** Week and day view: all-day items (not holidays) for the all-day section. */
export const allDaySpans = (items: readonly CalendarItem[]): ItemSpan[] =>
  monthSpans(
    items.filter((i) => i.allDay),
    "UTC",
  );

/** Week and day view: timed items split into per-day blocks. */
export function timedBlocks(
  items: readonly CalendarItem[],
  timeZone: string,
): Map<string, ItemBlock[]> {
  const byDay = new Map<string, ItemBlock[]>();
  for (const item of items) {
    if (isHoliday(item) || item.allDay || !item.startsAt || !item.endsAt) continue;
    const segments = timedSegments(new Date(item.startsAt), new Date(item.endsAt), timeZone);
    segments.forEach((segment, index) => {
      const blocks = byDay.get(segment.date) ?? [];
      blocks.push({
        id: `${item.id}@${segment.date}`,
        item,
        start: segment.start,
        end: segment.end,
        continuesBefore: index > 0,
        continuesAfter: index < segments.length - 1,
      });
      byDay.set(segment.date, blocks);
    });
  }
  return byDay;
}

const clock = (minutes: number) => {
  const m = minutes % (24 * 60);
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};

/** A block's start and end, "18:00 – 19:00"; "…" where it goes on from or into another day. */
export const blockTimes = (block: ItemBlock): string =>
  `${block.continuesBefore ? "…" : clock(block.start)} – ${block.continuesAfter ? "…" : clock(block.end)}`;

/** Public and school holidays per day of the range. */
export function holidaysByDay(items: readonly CalendarItem[], days: readonly string[]) {
  const result = new Map<string, DayHolidays>();
  for (const day of days) result.set(day, { public: [], school: [] });
  for (const item of items) {
    if (!isHoliday(item)) continue;
    for (const day of days) {
      if (day >= item.startDate && day <= item.endDate) {
        result.get(day)?.[item.holidayType].push(item);
      }
    }
  }
  return result;
}
